import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { qualityKeys } from '@/lib/api/quality.js';
import { ensureFreshPlatformJwt } from '@/lib/sso.js';

import { recoverFrom401 } from './stream-auth.js';

/**
 * Per-run SSE reader for `GET /v1/quality/runs/:id/stream`. Mirrors the
 * approach in `user-event-stream.ts`: fetch + bearer + ReadableStream +
 * reconnect backoff (native EventSource can't send the Authorization
 * header). Every connect attempt takes a freshly renewed bearer — a run can
 * stream for hours, far longer than the platform JWT's 15-minute life, so a
 * reconnect that reused the token it opened with would 401 forever.
 *
 * The stream is a hint, not a data source: every recognised event simply
 * invalidates the run-detail + runs-list + proposals queries so TanStack Query
 * refetches the authoritative snapshot. Events: `run.updated | result.updated |
 * stage.updated | review.updated | input.required | input.resolved | auth-flight.*`, payload
 * `{ runId, ... }` (input.* never carries the answered value — see InputRequiredPanel).
 */

const RECONNECT_BACKOFFS_MS = [1_000, 2_000, 5_000, 10_000];

/** Avoid turning a persistently broken SSE endpoint into a snapshot-refetch loop. */
const DISCONNECT_REFRESH_COOLDOWN_MS = 30_000;

const KNOWN_EVENTS = new Set([
  'run.updated',
  'result.updated',
  'stage.updated',
  'review.updated',
  'revision.updated',
  'outcome.corrected',
  'report.updated',
  'delivery.updated',
  'log.appended',
  'input.required',
  'input.resolved',
  'auth-flight.requested',
  'auth-flight.answered',
  'auth-flight.expired',
  'auth-flight.reset',
  'auth-flight.mismatch',
]);

interface QualityStreamEvent {
  type: string;
  runId?: string;
}

function isQualityEvent(value: unknown): value is QualityStreamEvent {
  if (!value || typeof value !== 'object') return false;
  const t = (value as { type?: unknown }).type;
  return typeof t === 'string' && KNOWN_EVENTS.has(t);
}

async function readStream(
  response: Response,
  onEvent: (type: string) => void,
  signal: AbortSignal,
): Promise<void> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() ?? '';
    for (const block of blocks) {
      let data = '';
      for (const line of block.split('\n')) {
        const trimmed = line.trimStart();
        if (trimmed.startsWith('data:')) data = trimmed.slice(5).trim();
        // ignore `event:`, `id:`, and `:` keepalive comments
      }
      if (!data) continue;
      try {
        const parsed: unknown = JSON.parse(data);
        if (isQualityEvent(parsed)) onEvent(parsed.type);
      } catch {
        /* malformed event, skip */
      }
    }
  }
}

/**
 * Subscribe to a run's SSE stream while its detail view needs invalidation hints.
 * Invalidates the run-detail and runs-list queries on every event. The caller keeps canonical
 * terminal runs enabled because corrections can advance their report revision after completion.
 */
export function useQualityRunStream(
  runId: string | undefined,
  enabled = true,
): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!runId || !enabled) return;
    const ctrl = new AbortController();
    let attempt = 0;
    let lastDisconnectRefreshAt = Number.NEGATIVE_INFINITY;

    /**
     * SSE is only an invalidation hint, so an unexpected EOF must trigger one authoritative
     * snapshot refresh before reconnecting. Otherwise an event lost with the connection can leave
     * the run result and report frozen until the operator reloads the whole page — most visibly
     * when a takeover settles at the same moment as the stream drops.
     */
    const refreshAfterDisconnect = (): void => {
      const now = Date.now();
      if (now - lastDisconnectRefreshAt < DISCONNECT_REFRESH_COOLDOWN_MS)
        return;
      lastDisconnectRefreshAt = now;
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      void qc.invalidateQueries({ queryKey: qualityKeys.proposals(runId) });
      void qc.invalidateQueries({
        queryKey: qualityKeys.reportBundleBase(runId),
      });
      void qc.invalidateQueries({ queryKey: qualityKeys.inputs(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.authFlights(runId) });
    };

    const invalidate = (type: string): void => {
      // Log lines arrive every ~500ms while a run is in flight; refetch ONLY the logs tail on them,
      // never the heavy run/proposal/bundle queries — those move on the coarse lifecycle events.
      if (type === 'log.appended') {
        void qc.invalidateQueries({ queryKey: qualityKeys.logs(runId) });
        return;
      }
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      // `review.updated` fires when a proposal version changes (synthesis ready, refine done) — the
      // review surface reads proposals under their own key, so invalidate it too or it won't refresh.
      void qc.invalidateQueries({ queryKey: qualityKeys.proposals(runId) });
      // The report bundle only changes when a per-case fragment lands, which is exactly what
      // `result.updated` (incl. the mobile video-ready ping) signals. Scope its invalidation to that
      // event so the other events (run/stage/review) don't trigger up to 3 redundant bundle refetches
      // (summary/cases/error-digest) each. See docs/incremental-per-case-reports.md.
      if (
        type === 'run.updated' ||
        type === 'result.updated' ||
        type === 'outcome.corrected' ||
        type === 'report.updated' ||
        type === 'delivery.updated'
      ) {
        void qc.invalidateQueries({
          queryKey: qualityKeys.reportBundleBase(runId),
        });
      }
      // `revision.updated` (MEDA-352) fires when a refine rewrite lands or a revision is
      // applied/dismissed — refetch the run's case revisions so the refine panel updates live.
      if (type === 'revision.updated') {
        void qc.invalidateQueries({
          queryKey: qualityKeys.caseRevisions(runId),
        });
      }
      // A request was just announced or answered — refetch the pending-inputs list so the panel
      // appears/updates without waiting for the next poll interval.
      if (type === 'input.required' || type === 'input.resolved') {
        void qc.invalidateQueries({ queryKey: qualityKeys.inputs(runId) });
      }
      // Environment-scoped authentication state never rides SSE itself; the event is only a safe
      // invalidation hint and the panel refetches the normalized, secret-free DTO.
      if (type.startsWith('auth-flight.')) {
        void qc.invalidateQueries({ queryKey: qualityKeys.authFlights(runId) });
      }
    };

    async function connectLoop(): Promise<void> {
      while (!ctrl.signal.aborted) {
        let streamOpened = false;
        try {
          const headers: Record<string, string> = {};
          const jwt = await ensureFreshPlatformJwt();
          if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
          const res = await fetch(
            `/v1/quality/runs/${encodeURIComponent(runId as string)}/stream`,
            { headers, signal: ctrl.signal },
          );
          if (res.status === 401) {
            if (!(await recoverFrom401(jwt))) return;
          } else {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            attempt = 0;
            streamOpened = true;
            await readStream(res, invalidate, ctrl.signal);
          }
        } catch (err) {
          if (ctrl.signal.aborted) return;
          // eslint-disable-next-line no-console
          console.warn(
            '[quality-run-stream] disconnected, will reconnect:',
            err,
          );
        }
        if (ctrl.signal.aborted) return;
        // Only an opened stream can lose an event. Auth renewal and failed connection attempts must not
        // consume the refresh cooldown before a later, real SSE disconnect.
        if (streamOpened) refreshAfterDisconnect();
        const delay =
          RECONNECT_BACKOFFS_MS[
            Math.min(attempt, RECONNECT_BACKOFFS_MS.length - 1)
          ];
        attempt += 1;
        await new Promise((r) => setTimeout(r, delay));
      }
    }

    void connectLoop();
    return () => ctrl.abort();
  }, [runId, enabled, qc]);
}
